import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { MessageKey } from '../i18n/messages';
import { TodoProgressKind } from '../web-api-client';
import { COMPLETED_TEXT, NOT_COMPLETED_TEXT, parseTaskStatus, partialText } from '../utils/taskStatus';

interface Props {
  /** Undefined/rỗng nghĩa là chưa từng nhập kết quả — hiện "Chưa có", khác với "chưa hoàn thành". */
  value: string | undefined;
  onChange: (status: string) => void;
  disabled?: boolean;
}

const KIND_LABEL_KEY: Record<TodoProgressKind, MessageKey> = {
  [TodoProgressKind.NotCompleted]: 'taskStatus.notCompleted',
  [TodoProgressKind.Partial]: 'taskStatus.partial',
  [TodoProgressKind.Completed]: 'taskStatus.completed'
};

const NONE_OPTION = 'none';

/**
 * Ô chọn trạng thái của một task. Ba dạng hợp lệ được máy chủ quy định bằng chuỗi tiếng Việt, nên
 * thành phần này luôn phát ra đúng chuỗi đó chứ không phát ra mã riêng — nhờ vậy không có tầng dịch
 * nào ở giữa để mà lệch.
 *
 * Chưa có kết quả (`value` rỗng) hiện riêng một dòng "Chưa có" thay vì mặc định thành "chưa hoàn
 * thành" — hai thứ khác nghĩa nhau: một cái là chưa từng nhập, một cái là đã nhập và kết quả là chưa
 * xong. Dòng "Chưa có" luôn hiện trong danh sách nhưng tự mờ đi khi đã có kết quả thật — máy chủ không
 * có endpoint nào xóa kết quả về rỗng ngoài Bắt đầu lại.
 *
 * Chọn "Partly done x/y" không gửi đi ngay: mở một popover nhập hai số + nút Xác nhận, giống cách popover
 * bắt đầu lại đang làm — tránh gửi lên máy chủ liên tục theo từng phím gõ.
 */
export function TaskStatusPicker({ value, onChange, disabled }: Props) {
  const { t } = useI18n();
  const hasValue = !!value;
  const parsed = parseTaskStatus(value);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftDone, setDraftDone] = useState(1);
  const [draftTotal, setDraftTotal] = useState(2);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;

    const onClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [popoverOpen]);

  const openPartialPopover = () => {
    setDraftDone(parsed.kind === TodoProgressKind.Partial ? parsed.done : 1);
    setDraftTotal(parsed.kind === TodoProgressKind.Partial ? parsed.total : 2);
    setPopoverOpen(true);
  };

  const confirmPartial = () => {
    onChange(partialText(draftDone, draftTotal));
    setPopoverOpen(false);
  };

  // Khi popover đang mở để chọn số, ô chọn hiện tạm "Partly done x/y" dù chưa gửi đi — không thì vừa
  // bấm vào sẽ thấy ô chọn nhảy về giá trị cũ ngay lập tức, trước khi kịp nhập số.
  const displayedKind = popoverOpen ? TodoProgressKind.Partial : hasValue ? parsed.kind : undefined;

  return (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      <div className="status-select-wrap" ref={wrapRef}>
        <select
          className="form-select form-select-sm w-auto"
          value={displayedKind ?? NONE_OPTION}
          disabled={disabled}
          onChange={event => {
            const next = event.target.value;

            if (next === NONE_OPTION) return;

            if (next === TodoProgressKind.Partial) {
              openPartialPopover();
              return;
            }

            onChange(next === TodoProgressKind.Completed ? COMPLETED_TEXT : NOT_COMPLETED_TEXT);
          }}
          aria-label={t('taskStatus.label')}
        >
          <option value={NONE_OPTION} disabled={hasValue}>
            {t('taskStatus.none')}
          </option>
          {Object.values(TodoProgressKind).map(kind => (
            <option key={kind} value={kind}>
              {t(KIND_LABEL_KEY[kind])}
            </option>
          ))}
        </select>

        {popoverOpen && (
          <div className="status-popover">
            <label className="restart-popover-label">{t('taskStatus.partial')}</label>

            <div className="d-flex gap-1 align-items-center">
              <input
                type="number"
                min={0}
                autoFocus
                className="form-control form-control-sm status-part"
                value={draftDone}
                aria-label={t('taskStatus.doneUnits')}
                onChange={event => setDraftDone(Math.max(0, Number(event.target.value) || 0))}
              />
              <span className="text-muted">/</span>
              <input
                type="number"
                min={1}
                className="form-control form-control-sm status-part"
                value={draftTotal}
                aria-label={t('taskStatus.totalUnits')}
                onChange={event => setDraftTotal(Math.max(1, Number(event.target.value) || 1))}
              />
            </div>

            <div className="restart-popover-actions">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPopoverOpen(false)}>
                {t('taskStage.restartCancel')}
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={confirmPartial}>
                {t('taskStage.restartConfirm')}
              </button>
            </div>
          </div>
        )}
      </div>

      {!popoverOpen && hasValue && parsed.kind === TodoProgressKind.Partial && (
        <button type="button" className="tag tag-warn partial-edit-badge" onClick={openPartialPopover}>
          {partialText(parsed.done, parsed.total)}
        </button>
      )}
    </div>
  );
}
